import React, { lazy } from 'react';
import { ContentfulModuleWrapper } from 'components/PageAssembly/ContentfulModuleWrapper/ContentfulModuleWrapper';
import { ContentType } from 'api/contentful/contentTypes.cloverdotcom';
import { applyEntryStyleAttributes } from 'components/EntryWrapper/EntryStyleWrapper';

/**
 * Lazily loads a React component.
 *
 * @param {Function} importFunc - A function that returns a Promise resolving to an object with a default property containing the React component.
 * @param {boolean} wrap - A boolean indicating whether to wrap the loaded component with the ContentfulModuleWrapper.
 * @returns {React.LazyExoticComponent<React.ComponentType<any>>} A lazily loaded React component.
 *
 * @example
 * const MyComponent = lazyLoadComponent(() => import('./MyComponent'), true);
 *
 * @throws Will throw an error if the component fails to load.
 */
export const lazyLoadComponent = (
  importFunc: () => Promise<{ default: React.ComponentType<any> }>,
  wrap: boolean,
): React.LazyExoticComponent<React.ComponentType<any>> => {
  return lazy(async () => {
    try {
      const { default: Component } = await importFunc();
      const componentWithStyles = applyEntryStyleAttributes(Component);

      return { default: wrap ? ContentfulModuleWrapper(componentWithStyles as any) : componentWithStyles };
    } catch (error) {
      console.error('Failed to load component:', error);
      return { default: () => <>Error loading the component.</> };
    }
  });
};

// Components that need to be wrapped with ContentfulModuleWrapper
export const WRAPPED_COMPONENTS = {
  [ContentType.TESTIMONIAL_MODULE]: () => import('components/Testimonial/TestimonialModule'),
  [ContentType.SYSTEM_DIAGRAM]: () => import('components/SystemDiagram/SystemDiagram'),
};

// Components that don't need to be wrapped with ContentfulModuleWrapper
export const NON_WRAPPED_COMPONENTS = {
  [ContentType.TABS_ASSEMBLY]: () => import('components/TabsModule/TabsModule'),
  [ContentType.MEDIA_MODULE]: () => import('components/TextMediaModule/TextMediaModule'),
  [ContentType.GRID_ASSEMBLY]: () => import('components/GridModule/GridModule'),
  [ContentType.FAQ_MODULE]: () => import('components/FaqModule/FaqModule'),
  [ContentType.CAROUSEL_ASSEMBLY]: () => import('components/CarouselModule/CarouselModule'),
};

// Create the lazy-loaded maps
const LAZY_WRAPPED_COMPONENTS = Object.keys(WRAPPED_COMPONENTS).reduce((acc, key) => {
  acc[key] = lazyLoadComponent(WRAPPED_COMPONENTS[key], true);
  return acc;
}, {} as { [key: string]: React.LazyExoticComponent<React.ComponentType<any>> });

const LAZY_NON_WRAPPED_COMPONENTS = Object.keys(NON_WRAPPED_COMPONENTS).reduce((acc, key) => {
  acc[key] = lazyLoadComponent(NON_WRAPPED_COMPONENTS[key], false);
  return acc;
}, {} as { [key: string]: React.LazyExoticComponent<React.ComponentType<any>> });

export const LAZY_MODULE_MAP: { [key: string]: React.LazyExoticComponent<React.ComponentType<any>> } = {
  ...LAZY_WRAPPED_COMPONENTS,
  ...LAZY_NON_WRAPPED_COMPONENTS,
};
